import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Modal } from 'src/components/Modal/Modal';
import { Select } from 'src/components/Select';
import { Button } from 'src/components/Button';
import { PermissionGuard } from 'src/components/PermissionGuard';
import { Container } from 'src/components/Container';

import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import {
  SUPPORT_TICKET_STATUSES,
  formatSupportStatusLabel,
  formatTicketUserName,
  getTicketUser,
  getTicketUserId,
  updateSupportTicketStatus,
  type SupportTicket,
  type SupportTicketStatus,
} from 'src/api/adminSupport';
import { convertDate, shortenString } from 'src/components/Table/helpers';
import { PERMISSIONS } from 'src/utils/permissions';
import { ROUTE_USERS } from 'src/utils/routes';

import {
  Breadcrumb,
  Card,
  CardTitle,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  HeadlineRow,
  MetaLine,
  StatusBadge,
  Title,
  TitleGroup,
  UserLink,
} from 'src/pages/Transactions/styled';
import CopyableValue from 'src/pages/Transactions/CopyableValue';

import {
  AttachmentGrid,
  AttachmentThumb,
  DescriptionBlock,
  StatusRow,
} from './styled';

type TicketDetailModalProps = {
  ticket: SupportTicket;
  onClose: () => void;
  onUpdated?: () => void;
};

const SectionSpacer = () => <div style={{ height: '1rem' }} />;

const TicketDetailModal: React.FC<TicketDetailModalProps> = ({
  ticket,
  onClose,
  onUpdated,
}) => {
  const [status, setStatus] = useState<string>(String(ticket.status ?? 'OPEN'));
  const [saving, setSaving] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = getTicketUser(ticket);
  const userId = getTicketUserId(ticket);
  const userName = formatTicketUserName(user);
  const attachments = ticket.attachment ?? [];
  const created = ticket.created_at ? convertDate(ticket.created_at) : null;
  const updated = ticket.updated_at ? convertDate(ticket.updated_at) : null;

  const hasChange = status !== String(ticket.status ?? 'OPEN');

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSupportTicketStatus(ticket.id, status as SupportTicketStatus);
      dispatch(
        setAppAlert({
          message: `Ticket set to ${formatSupportStatusLabel(status)}.`,
          isSuccess: true,
        }),
      );
      onUpdated?.();
      onClose();
    } catch (e: any) {
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? 'Failed to update the ticket',
          isSuccess: false,
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose}>
      <Breadcrumb>
        Support{ticket.id ? ` / ${shortenString(ticket.id)}` : ''}
      </Breadcrumb>

      <HeadlineRow>
        <TitleGroup>
          <Title>
            {ticket.subject || 'Support ticket'}
            {ticket.status ? (
              <StatusBadge $status={String(ticket.status)}>
                {formatSupportStatusLabel(String(ticket.status))}
              </StatusBadge>
            ) : null}
          </Title>
          {created ? (
            <MetaLine>
              Opened {created.date}
              {created.time ? ` · ${created.time}` : ''}
            </MetaLine>
          ) : null}
        </TitleGroup>
      </HeadlineRow>

      <Card>
        <CardTitle>Reporter</CardTitle>
        <DetailGrid>
          <DetailItem>
            <DetailLabel>Name</DetailLabel>
            {userId ? (
              <DetailValue>
                <UserLink
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate(`${ROUTE_USERS}/${userId}`);
                  }}
                >
                  {userName !== '—' ? userName : shortenString(String(userId))}
                </UserLink>
              </DetailValue>
            ) : (
              <DetailValue $muted={userName === '—'}>{userName}</DetailValue>
            )}
          </DetailItem>
          <DetailItem>
            <DetailLabel>Email</DetailLabel>
            <DetailValue $muted={!ticket.email}>{ticket.email || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>BxLend ID</DetailLabel>
            <DetailValue $muted={!user?.bxlend_id}>{user?.bxlend_id || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Account status</DetailLabel>
            {/* Absent for a ticket raised from the public contact form, which
                carries an email but no account. */}
            <DetailValue $muted={!user?.status}>{user?.status || '—'}</DetailValue>
          </DetailItem>
        </DetailGrid>
      </Card>
      <SectionSpacer />

      <Card>
        <CardTitle>Ticket</CardTitle>
        <DetailGrid>
          <DetailItem>
            <DetailLabel>Ticket ID</DetailLabel>
            <CopyableValue value={ticket.id} label="Ticket ID" />
          </DetailItem>
          <DetailItem>
            <DetailLabel>Last updated</DetailLabel>
            <DetailValue $muted={!updated}>
              {updated ? `${updated.date}${updated.time ? ` · ${updated.time}` : ''}` : '—'}
            </DetailValue>
          </DetailItem>
        </DetailGrid>
        <SectionSpacer />
        <DetailLabel>Description</DetailLabel>
        <DescriptionBlock>{ticket.description || '—'}</DescriptionBlock>
      </Card>
      <SectionSpacer />

      {attachments.length > 0 && (
        <>
          <Card>
            <CardTitle>Attachments ({attachments.length})</CardTitle>
            <AttachmentGrid>
              {attachments.map((url, index) => (
                <AttachmentThumb
                  key={url}
                  href={url}
                  target="_blank"
                  rel="noreferrer noopener"
                  title="Open full size"
                >
                  <img src={url} alt={`Attachment ${index + 1}`} loading="lazy" />
                </AttachmentThumb>
              ))}
            </AttachmentGrid>
          </Card>
          <SectionSpacer />
        </>
      )}

      <PermissionGuard permission={PERMISSIONS.TICKET_WRITE}>
        <Card>
          <CardTitle>Update status</CardTitle>
          <StatusRow>
            <Container width="12rem">
              <Select
                compact
                value={status}
                options={SUPPORT_TICKET_STATUSES.map((value) => ({
                  value,
                  label: formatSupportStatusLabel(value),
                }))}
                onChange={(e) => setStatus(e.currentTarget.value)}
              />
            </Container>
            <Button
              text={saving ? 'Saving...' : 'Save'}
              className="black"
              disabled={!hasChange || saving}
              onClick={handleSave}
            />
          </StatusRow>
        </Card>
      </PermissionGuard>
    </Modal>
  );
};

export default TicketDetailModal;
